import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AstppDirectTab } from './AstppDirectTab';
import { DidImportTab } from './DidImportTab';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Alert, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Select, MenuItem, FormControl, InputLabel, TextField, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Tooltip, IconButton, LinearProgress,
  Stack, Divider, Grid
} from '@mui/material';
import {
  UploadFile, People, Receipt, History, CheckCircle,
  ErrorOutline, Warning, ArrowBack, ArrowForward,
  Delete, Refresh, Visibility, DownloadDone, Cable
} from '@mui/icons-material';
import api from '../../services/api';

// ── ASTPP default column mappings ─────────────────────────────────────────────
const ASTPP_CUSTOMER_DEFAULTS: Record<string, string> = {
  name: 'company_name', email: 'email', phone: 'phone',
  code: 'username', balance: 'credit', credit_limit: 'credit_limit',
  currency: 'currency', status: 'status', rate_plan: 'tariff',
};
const ASTPP_CDR_DEFAULTS: Record<string, string> = {
  src: 'callerid', dst: 'callednum', start_time: 'starttime',
  duration: 'duration', billsec: 'billsec', status: 'disposition',
  cost: 'sellcost', buy_rate: 'buycost', sell_rate: 'sellrate',
  tenant: 'username',
  call_id: 'uniqueid',
};
const CUSTOMER_TARGET_FIELDS = [
  { key: 'name', label: 'Customer Name *' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'code', label: 'Account Code / Username' },
  { key: 'balance', label: 'Opening Balance' },
  { key: 'credit_limit', label: 'Credit Limit' },
  { key: 'currency', label: 'Currency' },
  { key: 'status', label: 'Status' },
  { key: 'rate_plan', label: 'Rate Plan Name (for mapping)' },
];
const CDR_TARGET_FIELDS = [
  { key: 'call_id', label: 'Unique Call ID (for diff update)' },
  { key: 'src', label: 'Caller (Source) *' },
  { key: 'dst', label: 'Destination *' },
  { key: 'start_time', label: 'Call Start Time *' },
  { key: 'duration', label: 'Duration (seconds)' },
  { key: 'billsec', label: 'Billed Seconds' },
  { key: 'status', label: 'Status / Disposition' },
  { key: 'cost', label: 'Cost (sell)' },
  { key: 'buy_rate', label: 'Buy Rate/Cost' },
  { key: 'sell_rate', label: 'Sell Rate' },
  { key: 'tenant', label: 'Customer / Username' },
];

// ── Column mapper component ───────────────────────────────────────────────────
const ColumnMapper: React.FC<{
  csvHeaders: string[];
  targetFields: { key: string; label: string }[];
  mapping: Record<string, string>;
  onChange: (m: Record<string, string>) => void;
  preview: Record<string, string>[];
  defaults: Record<string, string>;
}> = ({ csvHeaders, targetFields, mapping, onChange, preview, defaults }) => {
  const autoMap = () => {
    const m: Record<string, string> = {};
    for (const f of targetFields) {
      const def = defaults[f.key];
      if (def && csvHeaders.includes(def)) m[f.key] = def;
      else {
        const match = csvHeaders.find(h => h.toLowerCase() === f.key.toLowerCase()
          || h.toLowerCase().replace(/[_\s]/g, '') === f.key.toLowerCase().replace(/[_\s]/g, ''));
        if (match) m[f.key] = match;
      }
    }
    onChange(m);
  };
  useEffect(() => { if (Object.keys(mapping).length === 0) autoMap(); }, [csvHeaders.join(',')]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Map CSV Columns</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={autoMap}>Auto-Detect</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Target Field</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>CSV Column</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Preview (row 1)</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {targetFields.map(f => (
              <TableRow key={f.key}>
                <TableCell>{f.label}</TableCell>
                <TableCell>
                  <Select size="small" fullWidth displayEmpty value={mapping[f.key] || ''}
                    onChange={e => onChange({ ...mapping, [f.key]: e.target.value })}>
                    <MenuItem value=""><em>— skip —</em></MenuItem>
                    {csvHeaders.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: 12 }}>
                  {mapping[f.key] && preview[0] ? preview[0][mapping[f.key]] || '' : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Rate plan mapper component ────────────────────────────────────────────────
const RatePlanMapper: React.FC<{
  sourceNames: string[];
  ratePlans: any[];
  mapping: Record<string, string>;
  defaultPlanId: string;
  onChange: (m: Record<string, string>) => void;
  onDefaultChange: (id: string) => void;
  savedMappings: any[];
}> = ({ sourceNames, ratePlans, mapping, defaultPlanId, onChange, onDefaultChange, savedMappings }) => {
  useEffect(() => {
    if (sourceNames.length === 0 || Object.keys(mapping).length > 0) return;
    const m: Record<string, string> = {};
    for (const sn of sourceNames) {
      const saved = savedMappings.find(s => s.source_plan_name.toLowerCase() === sn.toLowerCase());
      if (saved?.target_rate_plan_id) m[sn] = saved.target_rate_plan_id;
      else {
        const match = ratePlans.find(rp => rp.name.toLowerCase() === sn.toLowerCase());
        if (match) m[sn] = match.id;
      }
    }
    if (Object.keys(m).length) onChange(m);
  }, [sourceNames.join(','), savedMappings.length]);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Rate Plan Matching</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Map source system rate plans to Edge rate plans. Saved mappings auto-fill on future imports.
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Default Rate Plan (used when no match found)</InputLabel>
        <Select label="Default Rate Plan (used when no match found)" value={defaultPlanId}
          onChange={e => onDefaultChange(e.target.value)}>
          <MenuItem value=""><em>— None (leave unassigned) —</em></MenuItem>
          {ratePlans.map(rp => <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>)}
        </Select>
      </FormControl>
      {sourceNames.length === 0 ? (
        <Alert severity="info">No rate plan column detected in the CSV — all customers will use the default plan above.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Source Rate Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Map to Edge Rate Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {sourceNames.map(sn => {
                const saved = savedMappings.find(s => s.source_plan_name.toLowerCase() === sn.toLowerCase());
                const isSaved = !!saved && saved.target_rate_plan_id === mapping[sn];
                return (
                  <TableRow key={sn}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{sn}</TableCell>
                    <TableCell>
                      <Select size="small" fullWidth displayEmpty value={mapping[sn] || ''}
                        onChange={e => onChange({ ...mapping, [sn]: e.target.value })}>
                        <MenuItem value=""><em>— Use default —</em></MenuItem>
                        {ratePlans.map(rp => <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>
                      {isSaved ? <Chip label="Saved" color="success" size="small" />
                        : mapping[sn] ? <Chip label="New" color="primary" size="small" />
                        : <Chip label="Unassigned" color="warning" size="small" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── File drop zone ────────────────────────────────────────────────────────────
const FileDropZone: React.FC<{ onFile: (f: File) => void; accept?: string; label?: string }> = ({
  onFile, accept = '.csv', label = 'Drop CSV file here or click to browse'
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const handle = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files[0]; if (f) onFile(f);
  };
  return (
    <Box
      onDrop={handle} onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onClick={() => inputRef.current?.click()}
      sx={{
        border: '2px dashed', borderColor: drag ? 'primary.main' : 'divider',
        borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
        bgcolor: drag ? 'action.hover' : 'background.default',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
      }}>
      <UploadFile sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
      <Typography>{label}</Typography>
      <Typography variant="caption" color="text.secondary">Max 100MB · CSV format</Typography>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
    </Box>
  );
};

// ── CUSTOMERS IMPORT TAB ──────────────────────────────────────────────────────
const CustomerImport: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [colMap, setColMap] = useState<Record<string, string>>({});
  const [ratePlanMap, setRatePlanMap] = useState<Record<string, string>>({});
  const [defaultPlanId, setDefaultPlanId] = useState('');
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [savedMappings, setSavedMappings] = useState<any[]>([]);
  const [sourceSystem, setSourceSystem] = useState('astpp');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/import/rate-plans').then(r => setRatePlans(r.data)).catch(() => {});
    api.get('/import/rate-plan-mappings?source_system=astpp').then(r => setSavedMappings(r.data)).catch(() => {});
  }, []);

  const parseFile = async (f: File) => {
    setLoading(true); setError('');
    try {
      setFile(f);
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/import/parse-headers', fd);
      setHeaders(data.headers); setPreview(data.preview); setTotalRows(data.total_rows);
      setColMap({});
      setStep(1);
    } catch (e: any) { setError(e.response?.data?.message || 'Failed to parse file'); }
    finally { setLoading(false); }
  };

  const getSourcePlanNames = () => {
    const col = colMap['rate_plan'];
    if (!col) return [];
    const names = new Set<string>();
    for (const row of preview) { if (row[col]) names.add(row[col]); }
    return Array.from(names);
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      fd.append('source_system', sourceSystem);
      fd.append('column_mapping', JSON.stringify(colMap));
      fd.append('rate_plan_mapping', JSON.stringify(ratePlanMap));
      if (defaultPlanId) fd.append('default_rate_plan_id', defaultPlanId);
      const { data } = await api.post('/import/customers', fd);
      setResult(data); setStep(3);
    } catch (e: any) { setError(e.response?.data?.message || 'Import failed'); }
    finally { setLoading(false); }
  };

  const steps = ['Upload CSV', 'Map Columns', 'Rate Plans', 'Done'];

  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {steps.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {step === 0 && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Source System</InputLabel>
              <Select label="Source System" value={sourceSystem} onChange={e => setSourceSystem(e.target.value)}>
                <MenuItem value="astpp">ASTPP</MenuItem>
                <MenuItem value="a2billing">A2Billing</MenuItem>
                <MenuItem value="voipswitch">VoIPSwitch</MenuItem>
                <MenuItem value="custom">Custom CSV</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FileDropZone onFile={parseFile} label="Drop customer export CSV (from ASTPP or other billing system)" />
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      )}

      {step === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Detected <strong>{totalRows} rows</strong> in <strong>{file?.name}</strong>.
            Map CSV columns to Edge fields below.
          </Alert>
          <ColumnMapper csvHeaders={headers} targetFields={CUSTOMER_TARGET_FIELDS}
            mapping={colMap} onChange={setColMap} preview={preview}
            defaults={sourceSystem === 'astpp' ? ASTPP_CUSTOMER_DEFAULTS : {}} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button onClick={() => setStep(0)} startIcon={<ArrowBack />}>Back</Button>
            <Button variant="contained" onClick={() => setStep(2)} endIcon={<ArrowForward />}>Next: Rate Plans</Button>
          </Box>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <RatePlanMapper
            sourceNames={getSourcePlanNames()}
            ratePlans={ratePlans}
            mapping={ratePlanMap}
            defaultPlanId={defaultPlanId}
            onChange={setRatePlanMap}
            onDefaultChange={setDefaultPlanId}
            savedMappings={savedMappings} />
          <Divider sx={{ my: 3 }} />
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Import Summary</Typography>
            <Typography variant="body2">File: <strong>{file?.name}</strong></Typography>
            <Typography variant="body2">Rows to import: <strong>{totalRows}</strong></Typography>
            <Typography variant="body2">Source system: <strong>{sourceSystem}</strong></Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setStep(1)} startIcon={<ArrowBack />}>Back</Button>
            <Button variant="contained" color="success" onClick={doImport}
              disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <People />}>
              Import {totalRows} Customers
            </Button>
          </Box>
        </Box>
      )}

      {step === 3 && result && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Import Complete</Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
            <Chip label={`${result.imported} imported`} color="success" />
            {result.failed > 0 && <Chip label={`${result.failed} failed`} color="error" />}
          </Stack>
          {result.errors?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Errors:</Typography>
              {result.errors.map((e: any, i: number) => (
                <Typography key={i} variant="caption" display="block">Row {e.row}: {e.error}</Typography>
              ))}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="outlined" onClick={() => { setStep(0); setFile(null); setResult(null); }}>
              Import Another File
            </Button>
            <Button variant="contained" onClick={onDone}>View History</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// ── CDR IMPORT TAB ────────────────────────────────────────────────────────────
const CdrImport: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [colMap, setColMap] = useState<Record<string, string>>({});
  const [sourceSystem, setSourceSystem] = useState('astpp');
  const [customers, setCustomers] = useState<any[]>([]);
  const [defaultTenantId, setDefaultTenantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/tenants?tier=customer&limit=200').then(r => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setCustomers(rows);
    }).catch(() => {});
  }, []);

  const parseFile = async (f: File) => {
    setLoading(true); setError('');
    try {
      setFile(f);
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/import/parse-headers', fd);
      setHeaders(data.headers); setPreview(data.preview); setTotalRows(data.total_rows);
      setColMap({});
      setStep(1);
    } catch (e: any) { setError(e.response?.data?.message || 'Failed to parse file'); }
    finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      fd.append('source_system', sourceSystem);
      fd.append('column_mapping', JSON.stringify(colMap));
      if (defaultTenantId) fd.append('default_tenant_id', defaultTenantId);
      const { data } = await api.post('/import/cdrs', fd);
      setResult(data); setStep(2);
    } catch (e: any) { setError(e.response?.data?.message || 'Import failed'); }
    finally { setLoading(false); }
  };

  const steps = ['Upload CSV', 'Map Columns', 'Done'];
  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {steps.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {step === 0 && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Source System</InputLabel>
              <Select label="Source System" value={sourceSystem} onChange={e => setSourceSystem(e.target.value)}>
                <MenuItem value="astpp">ASTPP</MenuItem>
                <MenuItem value="a2billing">A2Billing</MenuItem>
                <MenuItem value="freeswitch">FreeSWITCH</MenuItem>
                <MenuItem value="kamailio">Kamailio</MenuItem>
                <MenuItem value="custom">Custom CSV</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Default Customer (fallback)</InputLabel>
              <Select label="Default Customer (fallback)" value={defaultTenantId} onChange={e => setDefaultTenantId(e.target.value)}>
                <MenuItem value=""><em>— Skip rows with no customer match —</em></MenuItem>
                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <FileDropZone onFile={parseFile} label="Drop CDR export CSV (ASTPP cdr_table, FreeSWITCH CDRs, etc.)" />
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      )}

      {step === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Detected <strong>{totalRows} CDR rows</strong>. Map columns — rows with no resolvable customer will be skipped.
          </Alert>
          <ColumnMapper csvHeaders={headers} targetFields={CDR_TARGET_FIELDS}
            mapping={colMap} onChange={setColMap} preview={preview}
            defaults={sourceSystem === 'astpp' ? ASTPP_CDR_DEFAULTS : {}} />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setStep(0)} startIcon={<ArrowBack />}>Back</Button>
            <Button variant="contained" color="success" onClick={doImport}
              disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <DownloadDone />}>
              Import {totalRows} CDRs
            </Button>
          </Box>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      )}

      {step === 2 && result && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>CDR Import Complete</Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ mb: 1 }}>
            {result.inserted > 0   && <Chip label={`${result.inserted} new`}       color="success" />}
            {result.updated  > 0   && <Chip label={`${result.updated} updated`}    color="info" />}
            {result.unchanged > 0  && <Chip label={`${result.unchanged} unchanged`} variant="outlined" />}
            {result.skipped  > 0   && <Chip label={`${result.skipped} skipped`}    color="warning" />}
            {result.failed   > 0   && <Chip label={`${result.failed} failed`}      color="error" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {result.total} total rows processed · differential update
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="outlined" onClick={() => { setStep(0); setFile(null); setResult(null); }}>Import Another</Button>
            <Button variant="contained" onClick={onDone}>Generate Invoices</Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// ── GENERATE INVOICES TAB ─────────────────────────────────────────────────────
const GenerateInvoices: React.FC = () => {
  const [form, setForm] = useState({
    period_start: '', period_end: '', source_system: '', batch_id: '', due_days: '30',
  });
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewed, setPreviewed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [batches, setBatches] = useState<any[]>([]);
  const [rerateStats, setRerateStats] = useState<any>(null);
  const [rerateLoading, setRerateLoading] = useState(false);
  const [rerateResult, setRerateResult] = useState<any>(null);

  const loadRerateStats = async () => {
    try { const { data } = await api.get('/import/rerate/stats'); setRerateStats(data); } catch {}
  };
  const runRerate = async () => {
    setRerateLoading(true); setRerateResult(null);
    try { const { data } = await api.post('/import/rerate', { date_from: form.period_start ? form.period_start + 'T00:00:00Z' : undefined, date_to: form.period_end ? form.period_end + 'T23:59:59Z' : undefined }); setRerateResult(data); await loadRerateStats(); }
    catch (e: any) { setRerateResult({ error: e.response?.data?.message || 'Re-rate failed' }); }
    finally { setRerateLoading(false); }
  };

  useEffect(() => {
    loadRerateStats();
    api.get('/import/batches?type=cdrs&limit=50').then(r => setBatches(r.data?.data || [])).catch(() => {});
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0, 23, 59, 59);
    setForm(f => ({
      ...f,
      period_start: start.toISOString().slice(0, 10),
      period_end: end.toISOString().slice(0, 10),
    }));
  }, []);

  const doPreview = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/import/preview-invoices', {
        period_start: form.period_start + 'T00:00:00Z',
        period_end: form.period_end + 'T23:59:59Z',
        source_system: form.source_system || undefined,
        batch_id: form.batch_id || undefined,
      });
      setPreviewData(data); setPreviewed(true);
    } catch (e: any) { setError(e.response?.data?.message || 'Preview failed'); }
    finally { setLoading(false); }
  };

  const doGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const { data } = await api.post('/import/generate-invoices', {
        period_start: form.period_start + 'T00:00:00Z',
        period_end: form.period_end + 'T23:59:59Z',
        source_system: form.source_system || undefined,
        batch_id: form.batch_id || undefined,
        due_days: parseInt(form.due_days) || 30,
      });
      setResult(data);
    } catch (e: any) { setError(e.response?.data?.message || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const unrated = parseInt(rerateStats?.unrated_answered) || 0;
  return (
    <Box>
      {unrated > 0 && !rerateResult && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button size="small" variant="contained" color="warning" disabled={rerateLoading}
            onClick={runRerate}>{rerateLoading ? 'Rating...' : `Re-rate ${unrated.toLocaleString()} CDRs first`}</Button>}>
          <strong>{unrated.toLocaleString()} CDRs</strong> have no sell cost — they won't appear on invoices until re-rated.
          Current billed revenue: <strong>${parseFloat(rerateStats?.current_revenue || 0).toFixed(2)}</strong>
        </Alert>
      )}
      {rerateResult && !rerateResult.error && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRerateResult(null)}>
          Re-rated <strong>{rerateResult.updated?.toLocaleString()}</strong> CDRs. Revenue: <strong>${parseFloat(rerateResult.total_revenue||0).toFixed(2)}</strong> | Cost: <strong>${parseFloat(rerateResult.total_cost||0).toFixed(2)}</strong> | Profit: <strong>${parseFloat(rerateResult.total_profit||0).toFixed(2)}</strong>
        </Alert>
      )}
      {rerateResult?.error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRerateResult(null)}>{rerateResult.error}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Generated <strong>{result.generated} invoices</strong> successfully. They are in Draft status — review and approve in the Invoices page.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Billing Period & Filters</Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField fullWidth type="date" label="Period Start" size="small"
                  InputLabelProps={{ shrink: true }} value={form.period_start}
                  onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} />
                <TextField fullWidth type="date" label="Period End" size="small"
                  InputLabelProps={{ shrink: true }} value={form.period_end}
                  onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} />
              </Stack>
              <FormControl size="small" fullWidth>
                <InputLabel>Source System (optional)</InputLabel>
                <Select label="Source System (optional)" value={form.source_system}
                  onChange={e => setForm(f => ({ ...f, source_system: e.target.value }))}>
                  <MenuItem value=""><em>All sources</em></MenuItem>
                  <MenuItem value="astpp">ASTPP</MenuItem>
                  <MenuItem value="a2billing">A2Billing</MenuItem>
                  <MenuItem value="freeswitch">FreeSWITCH</MenuItem>
                  <MenuItem value="live">Live (real-time)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Specific Import Batch (optional)</InputLabel>
                <Select label="Specific Import Batch (optional)" value={form.batch_id}
                  onChange={e => setForm(f => ({ ...f, batch_id: e.target.value }))}>
                  <MenuItem value=""><em>All batches</em></MenuItem>
                  {batches.map(b => (
                    <MenuItem key={b.id} value={b.id}>
                      {new Date(b.created_at).toLocaleDateString()} — {b.filename} ({b.imported_rows} rows)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth size="small" label="Payment Due (days from today)" type="number"
                value={form.due_days} onChange={e => setForm(f => ({ ...f, due_days: e.target.value }))} />
              <Button variant="outlined" onClick={doPreview} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Visibility />}>
                Preview Billable Customers
              </Button>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          {previewed && (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {previewData.length} customers with unbilled CDRs
              </Typography>
              {previewData.length === 0 ? (
                <Alert severity="info">No unbilled CDRs found for this period and filter.</Alert>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Calls</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Minutes</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {previewData.map(row => (
                          <TableRow key={row.tenant_id}>
                            <TableCell>{row.tenant_name}</TableCell>
                            <TableCell align="right">{row.answered_calls?.toLocaleString()}</TableCell>
                            <TableCell align="right">{Math.round((row.total_seconds || 0) / 60).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              ${(parseFloat(row.subtotal) || parseFloat(row.cost_total) || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Total: <strong>${previewData.reduce((s, r) => s + (parseFloat(r.subtotal) || parseFloat(r.cost_total) || 0), 0).toFixed(2)}</strong>
                    </Typography>
                    <Button variant="contained" color="success" onClick={doGenerate}
                      disabled={generating || !!result}
                      startIcon={generating ? <CircularProgress size={16} /> : <Receipt />}>
                      Generate {previewData.length} Invoices
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// ── IMPORT HISTORY TAB ────────────────────────────────────────────────────────
const ImportHistory: React.FC = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [rollbackId, setRollbackId] = useState<string | null>(null);
  const [rolling, setRolling] = useState(false);
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/import/batches?limit=50${filterType ? `&type=${filterType}` : ''}`);
      setBatches(data.data || []);
    } catch { } finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const doRollback = async (id: string) => {
    setRolling(true);
    try {
      await api.delete(`/import/batches/${id}`);
      await load();
    } catch { } finally { setRolling(false); setRollbackId(null); }
  };

  const statusColor = (s: string) => s === 'completed' ? 'success' : s === 'rolled_back' ? 'default' : s === 'processing' ? 'info' : 'error';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by type</InputLabel>
          <Select label="Filter by type" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="customers">Customers</MenuItem>
            <MenuItem value="cdrs">CDRs</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={load}><Refresh /></IconButton>
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>File</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>OK</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Failed</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell />
            </TableRow></TableHead>
            <TableBody>
              {batches.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No import history yet</TableCell></TableRow>
              )}
              {batches.map(b => (
                <TableRow key={b.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(b.created_at).toLocaleString()}</TableCell>
                  <TableCell><Chip label={b.import_type} size="small" variant="outlined" /></TableCell>
                  <TableCell>{b.source_system}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.filename}</TableCell>
                  <TableCell align="right">{b.total_rows?.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{b.imported_rows?.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: b.failed_rows > 0 ? 'error.main' : 'text.secondary' }}>{b.failed_rows}</TableCell>
                  <TableCell><Chip label={b.status} size="small" color={statusColor(b.status)} /></TableCell>
                  <TableCell>
                    {b.status === 'completed' && (
                      <Tooltip title="Rollback this import">
                        <IconButton size="small" color="error" onClick={() => setRollbackId(b.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!rollbackId} onClose={() => setRollbackId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This will permanently delete all records (customers or CDRs) created by this import batch. This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => rollbackId && doRollback(rollbackId)}
            disabled={rolling}>
            {rolling ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null} Rollback
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── MAIN PAGE ──────────────────────────────────────────────────────────────────
const TABS = [
  { label: 'Import Customers', icon: <People /> },
  { label: 'Import CDRs', icon: <UploadFile /> },
  { label: 'Generate Invoices', icon: <Receipt /> },
  { label: 'Import History', icon: <History /> },
  { label: 'ASTPP Live', icon: <Cable /> },
  { label: 'Import DIDs', icon: <UploadFile /> },
];

export const BillingImportPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Billing Import</Typography>
        <Typography variant="body2" color="text.secondary">
          Import customers and CDRs from ASTPP or other billing systems, assign rate plans, and generate invoices.
          Super-admin only. All imports are tracked and can be rolled back.
        </Typography>
      </Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TABS.map((t, i) => <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />)}
        </Tabs>
      </Paper>
      <Paper sx={{ p: 3 }}>
        {tab === 0 && <CustomerImport onDone={() => setTab(3)} />}
        {tab === 1 && <CdrImport onDone={() => setTab(2)} />}
        {tab === 2 && <GenerateInvoices />}
        {tab === 3 && <ImportHistory />}
        {tab === 4 && <AstppDirectTab />}
        {tab === 5 && <DidImportTab />}
      </Paper>
    </Box>
  );
};
